import { MGErrorScreen } from '@mg/ui/components/MGErrorScreen';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { messageOf, RequestFailed } from '../../api/client.js';
import { useResetBroker } from '../../features/brokers/selection.js';

/**
 * The failures that mean "this broker cannot serve you", rather than "try again".
 *
 * Discovery offers every NATS in the cluster. Some of them have no JetStream, and some will
 * not take a token from this instance's Hydra — both are legitimate brokers that are simply
 * not ours. Picking one is one click, and undoing it is not obvious, because the choice is
 * remembered per browser and survives a reload.
 */
const BROKER_FAULTS = new Set(['BROKER_UNREACHABLE', 'HYDRA_REJECTED', 'STREAMS_UNAVAILABLE']);

/**
 * What a route shows when its loader fails: the server's own words, and a way forward. A 500
 * from the proxy while the API restarts is the everyday case, and retrying is right for it.
 */
export function ErrorScreen({ error, reset }: ErrorComponentProps): ReactNode {
  const router = useRouter();
  const resetBroker = useResetBroker();
  const message = messageOf(error);
  const brokerFault = error instanceof RequestFailed && BROKER_FAULTS.has(error.key ?? '');

  const retry = (): void => {
    reset();
    void router.invalidate();
  };

  // Retrying a broker that cannot serve this instance only fails again, so the button offers
  // the thing that does work: go back to the one this instance was deployed with.
  return brokerFault ? (
    <MGErrorScreen
      title="That broker cannot be read from here"
      subtitle={`${message} Every NATS in the cluster is offered, but only the ones that trust this instance's Hydra and run JetStream can be browsed.`}
      resetLabel="Back to the configured broker"
      resetError={() => {
        resetBroker();
        retry();
      }}
    />
  ) : (
    <MGErrorScreen
      title={
        error instanceof RequestFailed && error.status >= 500
          ? 'The API did not answer'
          : 'This page could not be loaded'
      }
      subtitle={message}
      resetLabel="Try again"
      resetError={retry}
    />
  );
}
